import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Activity, Bell, Mail, Users, FileText, Calendar, Upload, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
}

function StatCard({ icon: Icon, label, value, sub, color = "#06B6D4", testid }) {
  return (
    <div className="bg-white border border-black/8 rounded-2xl p-4 flex items-start gap-3" data-testid={testid}>
      <div className="rounded-xl p-2.5" style={{ background: `${color}1A` }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold">{label}</div>
        <div className="display text-2xl font-extrabold text-[#0A1128] mt-0.5 tabular-nums">{value}</div>
        {sub && <div className="text-[11px] text-[#64748B] mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

export default function Diagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/diagnostics");
      setData(r.data);
      if (!testEmail) setTestEmail(r.data?.email_provider?.sender_email || "");
    } catch (e) {
      toast.error("Impossible de charger le diagnostic");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const sendTestNotif = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/diagnostics/test-notification");
      if (r.data.ok) {
        toast.success("Notification test créée — vérifiez la cloche en haut à droite (15s max).");
        setTimeout(load, 1000);
      } else {
        toast.error("Échec de création de la notification test");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const sendTestEmail = async () => {
    if (!testEmail || !testEmail.includes("@")) return toast.error("Email destinataire invalide");
    setBusy(true);
    try {
      const r = await api.post("/admin/diagnostics/test-email", { to: testEmail });
      if (r.data.ok) toast.success(`Email envoyé via ${r.data.provider} à ${r.data.to}`);
      else toast.error(`Échec — provider: ${r.data.provider} · ${r.data.error || "vérifiez la console"}`);
      setTimeout(load, 1000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const purgeTestData = async () => {
    // First do a dry-run to preview what will be deleted
    setBusy(true);
    try {
      const dry = await api.post("/admin/maintenance/purge-test-data", { dry_run: true });
      const count = (dry.data?.matched || []).length;
      if (count === 0) return toast.success("Aucun compte de test à purger ✅");
      const sample = dry.data.matched.slice(0, 5).map((e) => e.email).join("\n  • ");
      const more = count > 5 ? `\n  … et ${count - 5} autre(s)` : "";
      const impact = dry.data.by_collection || {};
      const impactStr = Object.entries(impact).filter(([_, v]) => v > 0).map(([k, v]) => `${k}: ${v}`).join(" · ");
      const ok = window.confirm(
        `Vous allez SUPPRIMER ${count} compte(s) de test :\n  • ${sample}${more}\n\nImpact estimé : ${impactStr}\n\nCONFIRMER la suppression ?`
      );
      if (!ok) return;
      const r = await api.post("/admin/maintenance/purge-test-data", { dry_run: false });
      toast.success(`✅ ${r.data.deleted} documents supprimés (${count} clients de test)`);
      setTimeout(load, 800);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const importGoogleReviews = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/integrations/google-reviews/import");
      const imp = (r.data.imported || []).length;
      const upd = (r.data.updated || []).length;
      toast.success(`✅ Google Reviews — ${imp} importés, ${upd} mis à jour (${r.data.total_fetched} avis récupérés)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const migrateRefs = async () => {
    setBusy(true);
    try {
      const dry = await api.post("/admin/maintenance/migrate-document-refs?dry_run=true");
      const s = dry.data?.summary || {};
      const total = (s.proposals || 0) + (s.invoices_factures || 0) + (s.invoices_avoirs || 0) + (s.projects_cmd || 0);
      if (total === 0) return toast.success("Tous les documents sont déjà au nouveau format ✅");
      const examples = [
        ...(dry.data.changes?.proposals || []).slice(0, 3),
        ...(dry.data.changes?.invoices_factures || []).slice(0, 3),
        ...(dry.data.changes?.invoices_avoirs || []).slice(0, 2),
        ...(dry.data.changes?.projects_cmd || []).slice(0, 2),
      ].map((c) => `  • ${c.old}  →  ${c.new}`).join("\n");
      const ok = window.confirm(
        `Vous allez renommer ${total} document(s) :\n` +
        `  ${s.proposals || 0} devis · ${s.invoices_factures || 0} factures · ${s.invoices_avoirs || 0} avoirs · ${s.projects_cmd || 0} commandes\n\n` +
        `Exemples :\n${examples}\n\n` +
        `L'ancien numéro sera préservé dans "legacy_ref". CONFIRMER ?`
      );
      if (!ok) return;
      const r = await api.post("/admin/maintenance/migrate-document-refs?dry_run=false");
      const t = (r.data.summary?.proposals || 0) + (r.data.summary?.invoices_factures || 0) + (r.data.summary?.invoices_avoirs || 0) + (r.data.summary?.projects_cmd || 0);
      toast.success(`✅ ${t} document(s) renommés selon la nouvelle nomenclature`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (loading || !data) {
    return <div className="p-8 text-center text-[#64748B]" data-testid="diag-loading">Chargement du diagnostic…</div>;
  }

  const provider = data.email_provider || {};
  const providerOk = provider.provider !== "none";
  const notifs = data.notifications || {};
  const events = data.inter_account_events_24h || {};
  const activity = data.recent_activity || {};
  const invoiceSends = data.outbound_email_health?.last_invoice_sends || [];

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="admin-diagnostics">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold flex items-center gap-2"><Activity size={26} className="text-[#06B6D4]" />Diagnostic & Communication</h1>
          <p className="text-sm text-[#64748B] mt-1">Vérification temps réel des échanges entre comptes admin & clients.</p>
        </div>
        <Button onClick={load} variant="outline" data-testid="diag-refresh"><RefreshCw size={14} className="mr-1.5" />Rafraîchir</Button>
      </div>

      {/* Email provider */}
      <section className="bg-white border border-black/8 rounded-2xl p-5" data-testid="diag-email-section">
        <h2 className="display font-bold text-[#0A1128] flex items-center gap-2 mb-3"><Mail size={18} className="text-[#06B6D4]" />Pipeline Email</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className={`rounded-xl p-3 border ${providerOk ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B]">Provider actif</div>
            <div className={`display font-extrabold text-lg ${providerOk ? "text-green-700" : "text-red-700"} flex items-center gap-1.5 mt-1`}>
              {providerOk ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
              {provider.provider || "none"}
            </div>
          </div>
          <div className="rounded-xl p-3 border border-black/8">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B]">SMTP</div>
            <div className={`display font-extrabold text-lg ${provider.smtp_configured ? "text-[#0A1128]" : "text-[#94a3b8]"}`}>{provider.smtp_configured ? "Configuré" : "Non configuré"}</div>
          </div>
          <div className="rounded-xl p-3 border border-black/8">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B]">Resend</div>
            <div className={`display font-extrabold text-lg ${provider.resend_configured ? "text-[#0A1128]" : "text-[#94a3b8]"}`}>{provider.resend_configured ? "Clé OK" : "Pas de clé"}</div>
          </div>
          <div className="rounded-xl p-3 border border-black/8">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B]">Préférence</div>
            <div className="display font-extrabold text-lg text-[#0A1128]">{data.email_provider_preference || "auto"}</div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs font-bold text-[#64748B] uppercase tracking-widest">Tester l'envoi à</label>
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="votre@email.com" data-testid="diag-test-email-input" />
          </div>
          <Button onClick={sendTestEmail} disabled={busy || !providerOk} className="bg-[#06B6D4] text-[#0A1128] font-bold" data-testid="diag-send-test-email"><Mail size={14} className="mr-1.5" />Envoyer un email test</Button>
        </div>
        {invoiceSends.length > 0 && (
          <div className="mt-5">
            <div className="text-xs font-bold text-[#64748B] uppercase tracking-widest mb-2">5 derniers envois de facture</div>
            <div className="border border-black/8 rounded-xl divide-y divide-black/5">
              {invoiceSends.map((i, idx) => (
                <div key={idx} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div>
                    <div className="font-bold text-[#0A1128]">{i.ref}</div>
                    <div className="text-xs text-[#64748B]">{i.client_email} · Tentative {fmtDate(i.last_send_attempt_at)}</div>
                  </div>
                  <div>
                    {i.sent_at ? <span className="text-green-700 text-xs font-bold flex items-center gap-1"><CheckCircle2 size={12} />Envoyé</span> : <span className="text-red-700 text-xs font-bold flex items-center gap-1"><AlertTriangle size={12} />{i.last_send_error || "Échec"}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Notifications */}
      <section className="bg-white border border-black/8 rounded-2xl p-5" data-testid="diag-notif-section">
        <h2 className="display font-bold text-[#0A1128] flex items-center gap-2 mb-3"><Bell size={18} className="text-[#06B6D4]" />Système de notifications</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard icon={Bell} label="Total" value={notifs.total ?? 0} color="#0A1128" testid="diag-notif-total" />
          <StatCard icon={Bell} label="Non lues" value={notifs.unread ?? 0} sub="Badge cyan de la cloche" color="#06B6D4" testid="diag-notif-unread" />
          <StatCard icon={Bell} label="Dernières 24h" value={notifs.last_24h ?? 0} color="#10B981" testid="diag-notif-24h" />
          <StatCard icon={Bell} label="Dernières 7j" value={notifs.last_7d ?? 0} color="#94A3B8" testid="diag-notif-7d" />
        </div>
        {notifs.legacy_schema_count > 0 && (
          <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
            <strong>{notifs.legacy_schema_count}</strong> notification(s) en schéma legacy (sans champ <code>kind</code>) — corrigées automatiquement à la lecture, mais résiduelles en base.
          </div>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button onClick={sendTestNotif} disabled={busy} className="bg-[#06B6D4] text-[#0A1128] font-bold" data-testid="diag-send-test-notif"><Bell size={14} className="mr-1.5" />Créer une notif de test</Button>
          <p className="text-xs text-[#64748B]">Une notif de test apparaîtra dans la cloche sous 15 secondes (durée du polling).</p>
        </div>
        {notifs.recent && notifs.recent.length > 0 && (
          <div className="mt-5">
            <div className="text-xs font-bold text-[#64748B] uppercase tracking-widest mb-2">10 dernières notifications</div>
            <div className="border border-black/8 rounded-xl divide-y divide-black/5 max-h-64 overflow-y-auto">
              {notifs.recent.map((n, idx) => (
                <div key={idx} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-[#0A1128] truncate">{n.title || "(sans titre)"}</div>
                    <div className="text-[11px] text-[#64748B]">{n.kind || n.type || "—"} · {fmtDate(n.created_at)}</div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${n.read ? "bg-black/5 text-[#64748B]" : "bg-[#06B6D4] text-[#0A1128]"}`}>{n.read ? "Lue" : "Non lue"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Inter-account events */}
      <section className="bg-white border border-black/8 rounded-2xl p-5" data-testid="diag-events-section">
        <h2 className="display font-bold text-[#0A1128] flex items-center gap-2 mb-3"><Users size={18} className="text-[#06B6D4]" />Communication inter-comptes — dernières 24h</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard icon={Users} label="Leads" value={events.leads ?? 0} color="#06B6D4" testid="diag-evt-leads" />
          <StatCard icon={Calendar} label="RDV demandés" value={events.appointments ?? 0} color="#FFDCA8" testid="diag-evt-appts" />
          <StatCard icon={FileText} label="Briefs clients" value={events.briefs ?? 0} color="#A7F3D0" testid="diag-evt-briefs" />
          <StatCard icon={Upload} label="Docs uploadés (client)" value={events.client_document_uploads ?? 0} color="#94A3B8" testid="diag-evt-docs" />
          <StatCard icon={CheckCircle2} label="Devis signés" value={events.proposals_signed ?? 0} color="#10B981" testid="diag-evt-signed" />
        </div>
      </section>

      {/* Recent activity */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="diag-activity-section">
        <div className="bg-white border border-black/8 rounded-2xl p-4">
          <h3 className="display font-bold text-[#0A1128] text-sm flex items-center gap-1.5 mb-2"><FileText size={14} className="text-[#A7F3D0]" />Derniers briefs clients</h3>
          {(activity.briefs || []).length === 0 ? <p className="text-xs text-[#94A3B8] italic">Aucun brief soumis</p> : (
            <ul className="space-y-1.5 text-xs">
              {activity.briefs.map((b, i) => (
                <li key={i} className="border-b border-black/5 pb-1.5 last:border-0">
                  <div className="font-bold text-[#0A1128] truncate">{b.title}</div>
                  <div className="text-[10px] text-[#64748B]">{fmtDate(b.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white border border-black/8 rounded-2xl p-4">
          <h3 className="display font-bold text-[#0A1128] text-sm flex items-center gap-1.5 mb-2"><Upload size={14} className="text-[#94A3B8]" />Derniers documents clients</h3>
          {(activity.client_documents || []).length === 0 ? <p className="text-xs text-[#94A3B8] italic">Aucun upload récent</p> : (
            <ul className="space-y-1.5 text-xs">
              {activity.client_documents.map((d, i) => (
                <li key={i} className="border-b border-black/5 pb-1.5 last:border-0">
                  <div className="font-bold text-[#0A1128] truncate">{d.filename}</div>
                  <div className="text-[10px] text-[#64748B]">par {d.uploader_name} · {fmtDate(d.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white border border-black/8 rounded-2xl p-4">
          <h3 className="display font-bold text-[#0A1128] text-sm flex items-center gap-1.5 mb-2"><Users size={14} className="text-[#06B6D4]" />Derniers leads</h3>
          {(activity.leads || []).length === 0 ? <p className="text-xs text-[#94A3B8] italic">Aucun lead récent</p> : (
            <ul className="space-y-1.5 text-xs">
              {activity.leads.map((l, i) => (
                <li key={i} className="border-b border-black/5 pb-1.5 last:border-0">
                  <div className="font-bold text-[#0A1128] truncate">{l.name}</div>
                  <div className="text-[10px] text-[#64748B]">{l.source} · {fmtDate(l.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Maintenance & Integrations */}
      <section className="bg-white border border-black/8 rounded-2xl p-5" data-testid="diag-maintenance-section">
        <h2 className="display font-bold text-[#0A1128] flex items-center gap-2 mb-3"><AlertTriangle size={18} className="text-[#FFDCA8]" />Maintenance & Intégrations</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-xl border border-black/8 p-4 bg-[#FAFAFA]">
            <h3 className="display font-bold text-[#0A1128] text-sm">🧹 Purger les comptes de test</h3>
            <p className="text-xs text-[#64748B] mt-1 leading-relaxed">
              Supprime tous les comptes clients dont l&apos;email commence par <code className="bg-white px-1 rounded">autotest-</code>, <code className="bg-white px-1 rounded">welcometest-</code>, <code className="bg-white px-1 rounded">prebooking-</code>, <code className="bg-white px-1 rounded">uitest-</code>, <code className="bg-white px-1 rounded">diagtest-</code> ainsi que toutes leurs données rattachées (entreprises, devis, factures, RDV, briefs, documents, événements).
              Un dry-run est lancé en premier pour afficher l&apos;impact avant suppression effective.
            </p>
            <Button onClick={purgeTestData} disabled={busy} className="mt-3 bg-red-600 hover:bg-red-700 text-white font-bold" data-testid="diag-purge-test">🧹 Lancer la purge (dry-run + confirmation)</Button>
          </div>
          <div className="rounded-xl border border-black/8 p-4 bg-[#FAFAFA]">
            <h3 className="display font-bold text-[#0A1128] text-sm">⭐ Importer les avis Google</h3>
            <p className="text-xs text-[#64748B] mt-1 leading-relaxed">
              Récupère les 5 derniers avis de votre fiche Google (configurez le <strong>Google Place ID</strong> dans Paramètres). Les avis sont upsertés dans la collection témoignages (source <code className="bg-white px-1 rounded">google</code>) — idempotent par URL d&apos;avis.
              <span className="block text-[10px] text-[#FF6F61] mt-1">Nécessite <code className="bg-white px-1 rounded">GOOGLE_PLACES_API_KEY</code> dans le .env backend.</span>
            </p>
            <Button onClick={importGoogleReviews} disabled={busy} className="mt-3 bg-[#06B6D4] hover:bg-[#67E8F9] text-[#0A1128] font-bold" data-testid="diag-import-google-reviews">⭐ Importer maintenant</Button>
          </div>
          <div className="rounded-xl border border-black/8 p-4 bg-[#FAFAFA] lg:col-span-2">
            <h3 className="display font-bold text-[#0A1128] text-sm">🔁 Migrer la nomenclature des documents</h3>
            <p className="text-xs text-[#64748B] mt-1 leading-relaxed">
              Renomme tous les anciens devis (<code className="bg-white px-1 rounded">DEV-YYYY-NNNN</code>), factures (<code className="bg-white px-1 rounded">FAC-YYYY-NNN</code>), avoirs (<code className="bg-white px-1 rounded">AV-YYYY-NNN</code>) et commandes (<code className="bg-white px-1 rounded">CMD-YYYY-NNN</code>) vers la nouvelle nomenclature : <strong>BS-DEVYYYY-MM-NN</strong> (devis), <strong>BS-FACTYYYY-NNN</strong> (factures), <strong>BS-AVYYYY-NNN</strong> (avoirs), <strong>BS-CMDYYYY-NNN</strong> (commandes).
              <br/>L&apos;ancien numéro est conservé dans le champ <code className="bg-white px-1 rounded">legacy_ref</code> pour traçabilité. Les documents déjà au nouveau format ne sont pas modifiés. Un dry-run est lancé en premier pour afficher l&apos;impact.
            </p>
            <Button onClick={migrateRefs} disabled={busy} className="mt-3 bg-[#0A1128] hover:bg-[#14213d] text-white font-bold" data-testid="diag-migrate-refs">🔁 Lancer la migration (dry-run + confirmation)</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
